/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback } from "react";
import API, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Scales, Warning, ShieldCheck, CaretDown } from "@phosphor-icons/react";

// Ordered indicator list — grouped by weight so the checklist reads
// top-to-bottom in "look for this first" order. Weight labels match the
// backend TitleRiskFactor.weight buckets in
// /app/backend/services/hmlr_bg/risk_score.py.
const INDICATORS = [
  // CRITICAL
  { key: "has_bankruptcy", label: "Bankruptcy entry against a proprietor", weight: "critical" },
  // WARNING (non-standard entries)
  { key: "has_option_to_purchase", label: "Option to purchase in favour of a 3rd party", weight: "warning" },
  { key: "has_unilateral_notice", label: "Unilateral notice on the register", weight: "warning" },
  { key: "has_equitable_charge", label: "Equitable charge (unregistered debt)", weight: "warning" },
  { key: "has_sub_charge", label: "Sub-charge over an existing charge", weight: "warning" },
  // CAUTION (routine friction)
  { key: "has_registered_charge", label: "Registered charge (mortgage to redeem)", weight: "caution" },
  { key: "has_restriction", label: "Restriction on disposal", weight: "caution" },
  { key: "has_home_rights_notice", label: "Home rights notice (spouse consent)", weight: "caution" },
  { key: "has_restrictive_covenant", label: "Restrictive covenant on the title", weight: "caution" },
  { key: "has_leasehold", label: "Leasehold title (check term / ground rent)", weight: "caution" },
  { key: "has_green_out", label: "Part of the original title has been removed", weight: "caution" },
];

const CLASS_OF_TITLE_OPTIONS = [
  { value: "absolute", label: "Absolute (standard)" },
  { value: "good_leasehold", label: "Good leasehold" },
  { value: "qualified", label: "Qualified (limited guarantee)" },
  { value: "possessory", label: "Possessory (limited guarantee)" },
];

const WEIGHT_STYLES = {
  critical: { badge: "bg-rose-100 text-rose-700 border-rose-200", dot: "bg-rose-500" },
  warning: { badge: "bg-orange-100 text-orange-700 border-orange-200", dot: "bg-orange-500" },
  caution: { badge: "bg-amber-100 text-amber-800 border-amber-200", dot: "bg-amber-500" },
};

const LEVEL_STYLES = {
  clean: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", label: "Clean title" },
  caution: { bg: "bg-amber-50", text: "text-amber-800", border: "border-amber-200", label: "Caution" },
  warning: { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200", label: "Warning" },
  critical: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", label: "Critical" },
};

/**
 * Solicitor-entered Title Risk Score card. Interim tool that runs the
 * risk engine against manually-toggled register indicators — for use
 * TODAY, before HMLR Register Extract Service (RES) is live. When RES
 * goes live, this card will still be useful as an override / audit
 * trail (e.g. solicitor spotted something the parser missed).
 *
 * Backed by:
 *   GET  /api/cases/{caseId}/title-risk-manual
 *   POST /api/cases/{caseId}/title-risk-manual
 */
export default function ManualTitleRiskCard({ caseId, isSolicitor }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [indicators, setIndicators] = useState({});
  const [classOfTitle, setClassOfTitle] = useState("absolute");
  const [score, setScore] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const fetchSaved = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const { data } = await API.get(`/cases/${caseId}/title-risk-manual`);
      setIndicators(data.indicators || {});
      setClassOfTitle(data.class_of_title || "absolute");
      setScore(data.score || null);
      setUpdatedAt(data.updated_at || null);
    } catch (err) {
      // 404 = new case, treat as empty
      if (err.response?.status !== 404) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load title risk");
      }
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    fetchSaved();
  }, [fetchSaved]);

  const persistIfDirty = useCallback(async (nextIndicators, nextClass) => {
    setSaving(true);
    try {
      const { data } = await API.post(`/cases/${caseId}/title-risk-manual`, {
        indicators: nextIndicators,
        class_of_title: nextClass,
      });
      setScore(data.score || null);
      setUpdatedAt(data.updated_at || null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  }, [caseId]);

  const toggleIndicator = (key) => {
    const next = { ...indicators, [key]: !indicators[key] };
    setIndicators(next);
    persistIfDirty(next, classOfTitle);
  };

  const changeClass = (v) => {
    setClassOfTitle(v);
    persistIfDirty(indicators, v);
  };

  const level = score?.level || "clean";
  const styles = LEVEL_STYLES[level] || LEVEL_STYLES.clean;
  const totalFlagged = Object.values(indicators).filter(Boolean).length;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="manual-title-risk-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left"
        data-testid="manual-title-risk-toggle"
      >
        <div className="flex items-center gap-2">
          <Scales weight="bold" className="w-4 h-4 text-slate-500" />
          <h4 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            Title risk score
          </h4>
          {score && (
            <span
              className={`ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${styles.bg} ${styles.text} ${styles.border}`}
              data-testid="title-risk-level-badge"
            >
              {level === "clean" ? (
                <ShieldCheck weight="fill" className="w-3 h-3" />
              ) : (
                <Warning weight="fill" className="w-3 h-3" />
              )}
              {styles.label}
            </span>
          )}
        </div>
        <CaretDown weight="bold" className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {score?.summary && (
        <p className="text-[11px] text-slate-500 mt-1 ml-6" data-testid="title-risk-summary">
          {score.summary}
        </p>
      )}

      {open && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-4" data-testid="manual-title-risk-body">
          {loading ? (
            <p className="text-xs text-slate-400 py-2 text-center">Loading…</p>
          ) : (
            <>
              <div className="flex items-start gap-2 p-2 bg-slate-50 border border-slate-200 rounded text-[11px] text-slate-600">
                <span>
                  Tick each entry present on the HM Land Registry register. The score updates automatically. Use until HMLR RES is live —
                  {isSolicitor ? " changes are auto-saved." : " read-only."}
                </span>
              </div>

              <div>
                <label className="text-[11px] font-medium text-slate-600 mb-1 block">Class of title</label>
                <Select
                  value={classOfTitle}
                  onValueChange={changeClass}
                  disabled={!isSolicitor || saving}
                >
                  <SelectTrigger className="h-8 text-xs" data-testid="title-risk-class-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CLASS_OF_TITLE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value} data-testid={`title-risk-class-${o.value}`}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <fieldset className="space-y-1.5" disabled={!isSolicitor || saving}>
                <legend className="text-[11px] font-medium text-slate-600 mb-1">Register entries</legend>
                {INDICATORS.map((ind) => {
                  const s = WEIGHT_STYLES[ind.weight];
                  const checked = !!indicators[ind.key];
                  return (
                    <label
                      key={ind.key}
                      className={`flex items-center gap-2 p-2 rounded border text-[11px] cursor-pointer hover:bg-slate-50 ${checked ? "border-slate-300 bg-slate-50" : "border-slate-100"}`}
                      data-testid={`title-risk-indicator-${ind.key}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleIndicator(ind.key)}
                        className="h-3.5 w-3.5 rounded border-slate-300"
                        data-testid={`title-risk-check-${ind.key}`}
                      />
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.dot}`} />
                      <span className="flex-1 text-slate-800">{ind.label}</span>
                      <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${s.badge}`}>
                        {ind.weight}
                      </span>
                    </label>
                  );
                })}
              </fieldset>

              {score?.factors?.length > 0 && (
                <div className="pt-3 border-t border-slate-100" data-testid="title-risk-factors-list">
                  <p className="text-[11px] font-medium text-slate-600 mb-1.5">
                    Factors flagged ({score.factors.length})
                  </p>
                  <ul className="space-y-1">
                    {score.factors.map((f) => (
                      <li key={f.code} className="text-[11px] text-slate-700 flex items-start gap-1.5" data-testid={`title-risk-factor-${f.code}`}>
                        <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${WEIGHT_STYLES[f.weight].dot}`} />
                        <div>
                          <span className="font-medium">{f.label}</span>
                          {f.detail && <span className="text-slate-500"> — {f.detail}</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex items-center justify-between text-[10px] text-slate-400 pt-2">
                <span>
                  {totalFlagged === 0 ? "No entries ticked" : `${totalFlagged} entr${totalFlagged === 1 ? "y" : "ies"} ticked`}
                </span>
                {updatedAt && (
                  <span data-testid="title-risk-updated-at">
                    Saved {new Date(updatedAt).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
